/**
 * Taking a page away with you.
 *
 * Every register in this tool started life as a spreadsheet, and people still want one —
 * to read on a plane, to send to somebody who has no account, to sit beside a Salesforce
 * export while they reconcile the two. The tracker stays the source of truth; these files
 * are snapshots, and they carry the date in the name so nobody mistakes one for current.
 *
 * What comes out is what the filters left on screen, not the whole collection. Exporting
 * more than the page shows would surprise the person who filtered it down first.
 */

export type ExportColumn<T> = {
  header: string
  value: (row: T) => string | number | null | undefined
}

/**
 * Cells a spreadsheet would execute rather than display.
 *
 * Excel and Sheets treat a leading =, +, - or @ as the start of a formula, so a value that
 * begins with one is prefixed with an apostrophe — the spreadsheet convention for "this is
 * text". Numbers are passed through untouched: escaping those would land every count in
 * the file as a string, which breaks the sorting people download this to do.
 */
const FORMULA_START = /^[=+\-@\t\r]/

const cell = (raw: string | number | null | undefined): string => {
  if (raw === null || raw === undefined) return ''
  if (typeof raw === 'number') return Number.isFinite(raw) ? String(raw) : ''

  const text = FORMULA_START.test(raw) ? `'${raw}` : raw
  return /[",\n\r']/.test(text) || text !== raw
    ? `"${text.replace(/"/g, '""')}"`
    : text
}

/** Rows as CSV, headers first, CRLF between records because that is what Excel expects. */
export function toCsv<T>(rows: T[], columns: ExportColumn<T>[]): string {
  const header = columns.map((c) => cell(c.header)).join(',')
  const body = rows.map((row) => columns.map((c) => cell(c.value(row))).join(','))
  return [header, ...body].join('\r\n')
}

/** `missional-field-guide-2026-09-03.csv` — what it is, and when it was taken. */
export function exportFilename(label: string, extension: string, today = new Date()): string {
  const slug = label.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')
  const stamp = today.toISOString().slice(0, 10)
  return `missional-${slug}-${stamp}.${extension}`
}

/**
 * Hands the file to the browser.
 *
 * CSV gets a byte-order mark: without one Excel on Windows reads the file as its local
 * codepage, and every em dash in a decision note arrives as mojibake. Sheets and every
 * text editor ignore it.
 */
export function downloadText(filename: string, text: string, mime: string): void {
  const csv = mime.startsWith('text/csv')
  const BOM = '\uFEFF'
  const blob = new Blob([csv ? BOM + text : text], { type: `${mime};charset=utf-8` })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  // Revoking immediately can cancel the download in Safari, so let the click settle first.
  setTimeout(() => URL.revokeObjectURL(url), 1_000)
}

/** Rows straight to a dated CSV download. */
export function downloadCsv<T>(label: string, rows: T[], columns: ExportColumn<T>[]): void {
  downloadText(exportFilename(label, 'csv'), toCsv(rows, columns), 'text/csv')
}

/** A markdown document straight to a dated download. */
export function downloadMarkdown(label: string, markdown: string): void {
  downloadText(exportFilename(label, 'md'), markdown, 'text/markdown')
}

/**
 * The same rows as JSON, for handing to something that will read them rather than to a
 * spreadsheet.
 *
 * The keys are the CSV's own column headers, so the two formats can never drift into
 * describing different things. What JSON adds is the part a grid cannot hold: the
 * discussion arrives as nested objects with its replies intact, rather than as a block of
 * text in one cell that has to be parsed back apart.
 *
 * The envelope matters as much as the records. A file read weeks later, out of context,
 * has to be able to say what it is, when it was taken, and whether it is the whole
 * register or a slice of it — otherwise a filtered export gets mistaken for the lot.
 */
export type JsonExport = {
  export: string
  takenOn: string
  source: string
  scope: string
  count: number
  records: Record<string, unknown>[]
}

export function toJsonExport<T>(
  label: string,
  scope: string,
  rows: T[],
  columns: ExportColumn<T>[],
  extra?: (row: T) => Record<string, unknown>,
  today = new Date(),
): JsonExport {
  return {
    export: label,
    takenOn: today.toISOString().slice(0, 10),
    source: 'https://missional.portal.milestoneconsulting.tech',
    scope,
    count: rows.length,
    records: rows.map((row) => {
      const record: Record<string, unknown> = {}
      for (const column of columns) {
        const value = column.value(row)
        record[column.header] = value === undefined ? null : value
      }
      return { ...record, ...(extra ? extra(row) : {}) }
    }),
  }
}

/** A JSON export straight to a dated download. */
export function downloadJson(label: string, data: JsonExport): void {
  downloadText(exportFilename(label, 'json'), JSON.stringify(data, null, 2), 'application/json')
}
